import { useCallback, useMemo } from "react"
import { useFormik } from "formik"
import { useQuery } from "@tanstack/react-query"
import { toast } from "react-toastify"
import { Button, Form, FormFeedback, Label } from "reactstrap"
import * as Yup from "yup"

import apiClient from "api/axios"
import InputField from "components/Common/InputField"
import SelectField from "components/Common/SelectField"
import ThemeModal from "components/Common/ThemeModal"
import { useCreateAttendance, useEditAttendance } from "hooks/attendanceHook"

const toTimeInput = (dateTime, timeZone) => {
  if (!dateTime) return ""
  let normalized = String(dateTime).replace(" ", "T")
  if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    normalized += "Z"
  }
  const date = new Date(normalized)
  if (isNaN(date.getTime())) return ""
  const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date)
  const get = type => parts.find(p => p.type === type)?.value ?? "00"
  return `${get("hour").padStart(2, "0")}:${get("minute").padStart(2, "0")}`
}

const AttendanceManualModal = ({ modal, toggleModal, isEdit, editedData, timeZone }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateAttendance()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditAttendance()

  const employeesQuery = useQuery({
    queryKey: ["attendance-modal-employees"],
    queryFn: async () => {
      const res = await apiClient.get("/employees", {
        params: { all: 1, per_page: 1000, status: "active" },
      })
      console.log("[ManualAttendance] /employees response:", res.data)
      return res.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const employees = useMemo(() => {
    const body = employeesQuery.data
    if (!body) return []
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body)) return body
    return []
  }, [employeesQuery.data])

  const employeesLoading = employeesQuery.isLoading
  const employeesError = employeesQuery.error

  const validationSchema = Yup.object({
    employee_id: isEdit
      ? Yup.string()
      : Yup.string().required("Please select an employee"),
    date: isEdit ? Yup.string() : Yup.string().required("Please select a date"),
    check_in_time: Yup.string().required("Please enter check-in time"),
    check_out_time: Yup.string().test(
      "after-check-in",
      "Check-out must be after check-in",
      function (value) {
        if (!value) return true
        return value > this.parent.check_in_time
      }
    ),
    edit_reason: Yup.string()
      .required("Please provide a reason")
      .max(255, "Reason cannot exceed 255 characters"),
    remarks: Yup.string().max(255, "Remarks cannot exceed 255 characters"),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const payload = {
          check_in_time: values.check_in_time,
          check_out_time: values.check_out_time || undefined,
          edit_reason: values.edit_reason,
          remarks: values.remarks || undefined,
        }

        const action = isEdit
          ? editMutation({ id: editedData.id, ...payload })
          : createMutation({
              ...payload,
              employee_id: values.employee_id,
              date: values.date,
            })

        const res = await action
        toast.success(
          res?.message ??
            `Attendance ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to save attendance."
        )
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      employee_id: editedData?.employee_id || "",
      date: editedData?.date ? editedData.date.slice(0, 10) : "",
      check_in_time: toTimeInput(editedData?.check_in_at, timeZone),
      check_out_time: toTimeInput(editedData?.check_out_at, timeZone),
      edit_reason: "",
      remarks: editedData?.remarks || "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit,
  })

  const buttonText = useMemo(() => {
    const loading = editLoading || createLoading
    return `${isEdit ? "Save Changes" : "Add Attendance"}${loading ? "..." : ""}`
  }, [isEdit, editLoading, createLoading])

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Correct Attendance" : "Manual Attendance Entry"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {!isEdit && (
          <>
            <div className="mb-3">
              <SelectField
                label="Employee"
                name="employee_id"
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                loading={employeesLoading}
                options={employees}
                error={
                  (formik.touched.employee_id && formik.errors.employee_id) ||
                  (employeesError ? "Failed to load employees" : "")
                }
              />
              {(employeesLoading || employees.length > 0 || employeesError) && (
                <small className="text-muted d-block mt-1">
                  {employeesLoading
                    ? "Loading employees…"
                    : employeesError
                      ? "Failed to load employees. Check console for details."
                      : `Loaded ${employees.length} employee(s).`}
                </small>
              )}
            </div>

            <div className="mb-3">
              <Label htmlFor="date">
                Date <span className="text-danger">*</span>
              </Label>
              <InputField
                type="date"
                name="date"
                id="date"
                value={formik.values.date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={formik.touched.date && !!formik.errors.date}
              />
              {formik.touched.date && formik.errors.date && (
                <FormFeedback type="invalid">{formik.errors.date}</FormFeedback>
              )}
            </div>
          </>
        )}

        <div className="row">
          <div className="col-6 mb-3">
            <Label htmlFor="check_in_time">
              Check In <span className="text-danger">*</span>
            </Label>
            <InputField
              type="time"
              name="check_in_time"
              id="check_in_time"
              value={formik.values.check_in_time}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.check_in_time && !!formik.errors.check_in_time
              }
            />
            {formik.touched.check_in_time && formik.errors.check_in_time && (
              <FormFeedback type="invalid">
                {formik.errors.check_in_time}
              </FormFeedback>
            )}
          </div>

          <div className="col-6 mb-3">
            <Label htmlFor="check_out_time">Check Out</Label>
            <InputField
              type="time"
              name="check_out_time"
              id="check_out_time"
              value={formik.values.check_out_time}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.check_out_time && !!formik.errors.check_out_time
              }
            />
            {formik.touched.check_out_time && formik.errors.check_out_time && (
              <FormFeedback type="invalid">
                {formik.errors.check_out_time}
              </FormFeedback>
            )}
          </div>
        </div>

        <div className="mb-3">
          <Label htmlFor="edit_reason">
            Reason <span className="text-danger">*</span>
          </Label>
          <InputField
            type="text"
            name="edit_reason"
            id="edit_reason"
            placeholder="e.g. Forgot to check in"
            value={formik.values.edit_reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.edit_reason && !!formik.errors.edit_reason}
          />
          {formik.touched.edit_reason && formik.errors.edit_reason && (
            <FormFeedback type="invalid">
              {formik.errors.edit_reason}
            </FormFeedback>
          )}
        </div>

        <div className="mb-3">
          <Label htmlFor="remarks">Remarks</Label>
          <InputField
            type="textarea"
            name="remarks"
            id="remarks"
            value={formik.values.remarks}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            invalid={formik.touched.remarks && !!formik.errors.remarks}
          />
          {formik.touched.remarks && formik.errors.remarks && (
            <FormFeedback type="invalid">{formik.errors.remarks}</FormFeedback>
          )}
        </div>

        <div className="text-end mt-3">
          <Button
            color="success"
            type="submit"
            disabled={editLoading || createLoading}
          >
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AttendanceManualModal
